/**
 * AuthContext — provides current user state to the whole app.
 *
 * On mount, calls GET /me. If signed in, populates user. If 401, stays null.
 * Exposes refresh() for re-fetching after sign-in, and signOut() which
 * calls POST /auth/signout and clears state.
 *
 * Sign-in flow itself (test-login form, real Entra SSO later) lives in
 * separate components that call refresh() on success.
 */
import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { api, ApiError, authToken } from '../lib/apiClient.ts';

export interface SessionUser {
  id: string;
  email: string;
  displayName: string;
  isAdmin: boolean;
  /**
   * Headshot URL pulled from the OJB roster (ojb_team table) when the
   * signed-in user has a matching roster row. Null when the user
   * isn't on the roster yet, when the roster row exists but has no
   * photo, or while the Graph sync is still pending.
   *
   * Render with <AttendeeAvatar name={displayName} headshotUrl={...} />
   * which falls back to a pine-tinted initials circle automatically.
   */
  headshotUrl?: string | null;
  /**
   * Count of sign-ins in the onboarding-tour window. useTour checks
   * < 3 to decide whether to fire any page's tour; 3+ means the
   * tour is permanently done for this user.
   */
  tourSigninsSeen: number;
  /**
   * Persisted split-pane position for the meeting editor. 0..100;
   * 50 = even split. Updated via PATCH /me from EditorSplitPane on
   * drag-end.
   */
  editorSplitPercent?: number;
  /**
   * ISO timestamp of when the current session expires. Refreshed
   * on every /me call (which is also the call that performs the
   * sliding-window extension server-side). Used by
   * SessionExpiryWatcher to render an "expires soon" banner +
   * proactively re-fetch /me before expiry to roll the session
   * forward without a hard re-sign-in. May be null if the server
   * couldn't determine the expiry (e.g. cookie path on a request
   * that didn't touch the session).
   */
  sessionExpiresAt?: string | null;
}

interface AuthContextValue {
  user: SessionUser | null;
  isLoading: boolean;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
  /**
   * True for the render immediately after signOut() succeeded. The
   * root router uses this to show the "You're signed out" landing
   * instead of bouncing back to the Microsoft sign-in prompt. Cleared
   * by clearJustSignedOut() when the user clicks "Sign back in".
   */
  justSignedOut: boolean;
  clearJustSignedOut: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  // CRITICAL: isLoading must ONLY be true during the very first
  // /me call, never on subsequent background refreshes. The App
  // root unmounts every signed-in subtree (including
  // SessionExpiryWatcher) when isLoading is true to render the
  // "Loading…" splash. If a background refresh flipped isLoading
  // back to true, the watcher would unmount, lose its
  // expiredRefreshFiredRef guard, remount with a fresh ref, see
  // "expired" again, fire another refresh — and loop. Track the
  // initial load separately from background refreshes so we
  // never re-trigger that splash.
  const [isLoading, setIsLoading] = useState(true);
  const [justSignedOut, setJustSignedOut] = useState(false);

  const refresh = useCallback(async () => {
    // Defensive 10s timeout: if /me hangs (Railway api mid-deploy,
    // network blip, infrastructure outage), don't trap the user
    // on "Loading…" forever. Treat as no-user so the App falls
    // through to SignInPage where the user has a recovery
    // affordance ("Sign in with Microsoft" → fresh attempt).
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 10_000);
    try {
      const me = await api.get<SessionUser>('/me', undefined, controller.signal);
      setUser(me);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setUser(null);
      } else {
        // Network error, abort, or unexpected response — treat as
        // signed-out so the user lands on SignInPage and can
        // retry interactively.
        setUser(null);
      }
    } finally {
      window.clearTimeout(timeoutId);
      // Only the initial mount flips isLoading false here; the
      // mount useEffect below clears it after the first call. We
      // never set isLoading=true from refresh(), see the comment
      // on the state declaration above.
      setIsLoading(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.post('/auth/signout');
    } catch {
      // Even if server-side sign-out fails, clear local state
    }
    authToken.clear();
    setUser(null);
    setJustSignedOut(true);
  }, []);

  const clearJustSignedOut = useCallback(() => setJustSignedOut(false), []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <AuthContext.Provider
      value={{ user, isLoading, refresh, signOut, justSignedOut, clearJustSignedOut }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
